import { Property as IgnitionProperty, EndpointError } from '@square/ignition';
import { Link, AnchoredHeading } from '@squareup/dex-ui';
import { Heading20, Box, Paragraph20 } from '@squareup/dex-ui-shared-base';
import { humanizeName } from '@squareup/dex-utils-text';
import React, { FunctionComponent } from 'react';

import {
  PropertyCategory,
  PropertyRow,
} from '../../PropertyRow/src/PropertyRow';
import { PropertyTable } from '../../PropertyTable/src/PropertyTable';

import { usePropertyHeadingAnchor, useTableName } from './hooks';
import styles from './reference-property-table.module.css';

interface ReferencePropertyTableProps {
  properties: IgnitionProperty[] | EndpointError[] | undefined;
  propertyCategory: PropertyCategory;
  showExampleCode?: boolean;
  narrowView?: boolean;
}

const ReferencePropertyTable: FunctionComponent<
  ReferencePropertyTableProps
> = ({
  properties,
  propertyCategory,
  showExampleCode = false,
  narrowView = false,
}) => {
  const tableName = useTableName(propertyCategory);
  const headingAnchor = usePropertyHeadingAnchor(propertyCategory);

  const validProperties = [];
  // Can't call .filter due to tsc error due to the union type
  // In general, we should rework this so that EndpointErrors
  // can be passed in more easily
  for (const property of properties || []) {
    // EndpointErrors are all allowed
    if ((property as EndpointError).code) {
      validProperties.push(property);
    } else if (!(property as IgnitionProperty).isRetired) {
      validProperties.push(property);
    }
  }

  return (
    <Box margin={{ bottom: '4x', top: '4x' }}>
      <Box
        className={styles['title-container']}
        border={{ line: { bottom: 'standard' } }}
        padding={{ bottom: '2x' }}
      >
        <span className={styles['section-title']}>
          <AnchoredHeading
            trackingId={`reference-object-content-${headingAnchor}`}
            anchor={headingAnchor}
            aria-label={`Copy link to ${tableName}`}
            aria-describedby={
              'Copies a link directly to this property into your clipboard'
            }
          >
            <Heading20>{humanizeName(tableName)}</Heading20>
          </AnchoredHeading>
        </span>
        {showExampleCode && (
          <Link
            trackingId="navigate-to-reference-example-code"
            href="#example-code"
            aria-label="Example Code"
            className={styles['example-link']}
          >
            <Paragraph20>Example code</Paragraph20>
          </Link>
        )}
      </Box>
      <PropertyTable>
        {validProperties.map((property, i) => {
          return (
            <PropertyRow
              category={propertyCategory}
              key={property.name}
              property={property as IgnitionProperty}
              narrowView={narrowView}
              showBottomBorder={narrowView && i !== validProperties.length - 1}
            />
          );
        })}
      </PropertyTable>
    </Box>
  );
};

export { ReferencePropertyTable };
export { PropertyCategory } from '../../PropertyRow/src/PropertyRow';
